import {
  ELSCommonConfig,
  ELSTokenHelper
} from '@els/els-ui-common-react';
import {
  convertIsbnsToNormalizedString,
} from '../../utilities/app.utilities';
import {
  getInstitution,
  getIsbns
} from '../app/app-state-selectors.utilities';
import {
  isInstructor,
  isStudent
} from '../../utilities/common.utilities';
import { AppState } from '../app/app-state.types';
import { Application } from '../../apis/eols-app-link/eols-app-link.constants';
import { getSemester } from '../../utilities/adobe-analytics-utilities';
import {
  AdobeAccessTypes,
  AdobeAnalyticsPageData,
  AdobeLoginStatus
} from '../../models/adobe-analytics.models';
import {
  getBrand,
} from '../../pages/ai-chat/ai-chat.utilities';

const getEvolveUserEmail = (appState: AppState): string => {
  const { evolveUser } = appState;
  if (!evolveUser) {
    return null;
  }
  const evolveUserData = JSON.parse(evolveUser.userData);
  if (!evolveUserData || !evolveUserData.email) {
    return null;
  }
  return evolveUserData.email;
};

export const getTestVisitorId = (visitorId: string, appState: AppState): string => {
  const evolveUserEmail = getEvolveUserEmail(appState);
  const eolsUserEmail = appState.eolsUser ? appState.eolsUser.emailAddress : '';
  const { testUserEmailDomains } = ELSCommonConfig;
  const email = evolveUserEmail || eolsUserEmail;
  if (!email) {
    return visitorId;
  }
  const emailSplit = email.split('@');
  if (emailSplit.length !== 2) {
    return visitorId;
  }
  if (testUserEmailDomains.includes(emailSplit[1])) {
    return `test-${visitorId}`;
  }
  if (ELSTokenHelper.isMasqueradeUser()) {
    return `masquerade-${visitorId}`;
  }
  return visitorId;
};

type PendoAnalyticsConfig = {
  visitor: {
    id: string;
    webuserID: string;
    email: string;
    accessType: string;
    pageProduct: string;
    pageLanguage: string;
    pageEnvironment: string;
    courseId: string;
    isbn: string;
    application: string;
    product: string;
  };
  account: {
    id: string;
    name: string;
  };
}

export const getAnalyticsProps = (appState: AppState): {
  accessType: AdobeAccessTypes;
  prefixedUserId: string;
  prefixedInstitutionName: string;
  prefixedInstitutionId: string;
  loginStatus: AdobeLoginStatus;
  userEmail: string;
} => {
  const evolveUserId = appState.evolveUser ? appState.evolveUser.userName.toString() : null;
  const eolsUserId = appState.userId ? appState.userId.toString() : null;
  const institution = getInstitution(appState);

  const institutionName = institution ? institution.name : null;
  const institutionId = institution ? institution.id.toString() : null;
  let userEmail = getEvolveUserEmail(appState);
  if (!userEmail) {
    userEmail = appState.eolsUser ? appState.eolsUser.emailAddress : null;
  }

  const prefixedUserId = evolveUserId ? `evo:${evolveUserId}` : `eols:${eolsUserId}`;
  const prefixedInstitutionName = evolveUserId ? `evo:${institutionName}` : `eols:${institutionName}`;
  const prefixedInstitutionId = evolveUserId ? `evo:${institutionId}` : `eols:${institutionId}`;
  const loginStatus = evolveUserId || eolsUserId ? AdobeLoginStatus.LOGGED_IN : AdobeLoginStatus.ANONYMOUS;
  const accessType = evolveUserId || eolsUserId ? AdobeAccessTypes.REG_USER : AdobeAccessTypes.ANON_GUEST;

  return {
    accessType,
    prefixedUserId: getTestVisitorId(prefixedUserId, appState),
    prefixedInstitutionName,
    prefixedInstitutionId,
    loginStatus,
    userEmail
  };
};

export const getPendoConfig = (appState: AppState): PendoAnalyticsConfig => {
  const analyticsProps = getAnalyticsProps(appState);
  return {
    visitor: {
      id: analyticsProps.prefixedUserId,
      webuserID: analyticsProps.prefixedUserId,
      email: analyticsProps.userEmail,
      accessType: appState.userRole,
      pageProduct: window.pageData.page.productName,
      pageLanguage: window.pageData.page.language,
      pageEnvironment: window.pageData.page.environment,
      courseId: appState.courseSectionId,
      isbn: appState.tokenIsbns,
      application: Application.NHE_AI,
      product: getBrand(appState)
    },
    account: {
      id: analyticsProps.prefixedInstitutionId,
      name: analyticsProps.prefixedInstitutionName,
    }
  };
};

export const getDefaultAdobeAnalyticsPageData = (appState: AppState): Partial<AdobeAnalyticsPageData> => {
  const analyticsProps = getAnalyticsProps(appState);
  return {
    education: {
      courseId: appState.courseSectionId,
      courseName: appState.currentCourse ? appState.currentCourse.courseName : '',
      isbn: convertIsbnsToNormalizedString(getIsbns(appState)),
      studentId: isStudent(appState.userRole) ? appState.userId : '',
      instructorId: isInstructor(appState.userRole) ? appState.userId : '',
      semester: getSemester(),
    },
    visitor: {
      ipAddress: appState.ipAddress,
      accountNumber: appState.ecrId || analyticsProps.prefixedInstitutionId,
      loginStatus: analyticsProps.loginStatus,
      userId: analyticsProps.prefixedUserId,
      accountId: analyticsProps.prefixedInstitutionId,
      accountName: analyticsProps.prefixedInstitutionName,
      departmentId: '',
      departmentName: '',
      usagePathInfo: `${analyticsProps.prefixedUserId}U|D|${analyticsProps.prefixedInstitutionId}A|scwP|eols, PL-N|${analyticsProps.loginStatus}${analyticsProps.accessType}`,
      accessType: analyticsProps.accessType,
      details: 'sai:true'
    }
  };
};
